import React from 'react';

interface Props {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  showActionFilters?: boolean;
  showActiveEvents?: boolean;
  entityType: 'request' | 'events';
  onClick: (page: number) => void;
  setShowFilters: (v: boolean) => void;
  setShowActives?: (v: boolean) => void;
  setCreateRequest?: (v: boolean) => void;
  onAddEvent?: () => void;
}

const MobileListFooter: React.FC<Props> = ({
  currentPage,
  totalPages,
  totalItems,
  showActionFilters = false,
  showActiveEvents,
  entityType,
  onClick,
  setShowFilters,
  setShowActives,
  setCreateRequest,
  onAddEvent
}) => (
  <div
    className="fixed bottom-0 bg-gray-100 border-t border-gray-200 z-50 flex flex-row flex-nowrap items-center justify-between py-1"
    style={{
      left: '58px',
      right: 0,
      background: 'white',
      borderTop: '1px solid #ddd',
      padding: '0.5rem 0.5rem',
      zIndex: 1000,
      boxSizing: 'border-box',
      maxWidth: '100vw',
      overflowX: 'auto',
      position: 'fixed',
      minHeight: '56px'
    }}
  >
    {/* Left side: creación y filtros */}
    <div className="flex flex-row flex-nowrap items-center gap-1 whitespace-nowrap">
      {entityType === 'events' && onAddEvent && (
        <button
          aria-label="Add new event"
          className="rounded w-9 h-9 flex items-center justify-center text-xl"
          onClick={onAddEvent}
          type="button"
        >
          <svg
            className="w-6 h-6 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14m-7 7V5" />
          </svg>
        </button>
      )}

      {entityType === 'request' && setCreateRequest && (
        <button
          aria-label="Create Request"
          className="rounded w-9 h-9 flex items-center justify-center text-xl"
          onClick={() => setCreateRequest(true)}
          type="button"
        >
          <svg
            className="w-6 h-6 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14m-7 7V5" />
          </svg>
        </button>
      )}

      {/* Botones de filtros solo si showActionFilters activo */}
      {showActionFilters && (
        <>
          <button
            aria-label="Filters"
            className="rounded w-9 h-9 flex items-center justify-center text-xl"
            onClick={() => setShowFilters(true)}
            type="button"
          >
            <svg
              className="w-6 h-6 text-gray-800 dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
                d="M18.796 4H5.204a1 1 0 0 0-.753 1.659l5.302 6.058a1 1 0 0 1 .247.659v4.874a.5.5 0 0 0 .2.4l3 2.25a.5.5 0 0 0 .8-.4v-7.124a1 1 0 0 1 .247-.659l5.302-6.059c.566-.646.106-1.658-.753-1.658Z"
              />
            </svg>
          </button>
          {entityType === 'request' && (
            <button
              aria-label="Active filters"
              className="rounded w-9 h-9 flex items-center justify-center text-xl"
              onClick={() => setShowActives && setShowActives(true)}
              type="button"
            >
              <svg
                className="w-6 h-6 text-gray-800 dark:text-white"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                {showActiveEvents ? (
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3.933 13.909A4.357 4.357 0 0 1 3 12c0-1 4-6 9-6m7.6 3.8A5.068 5.068 0 0 1 21 12c0 1-3 6-9 6-.314 0-.62-.014-.918-.04M5 19 19 5m-4 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                ) : (
                  <>
                    <path stroke="currentColor" strokeWidth="2" d="M21 12c0 1.2-4.03 6-9 6s-9-4.8-9-6c0-1.2 4.03-6 9-6s9 4.8 9 6Z" />
                    <path stroke="currentColor" strokeWidth="2" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </>
                )}
              </svg>
            </button>
          )}
        </>
      )}
    </div>

    {/* Right side: pagination and records */}
    <div className="flex items-center gap-0 whitespace-nowrap ml-2">
      <button
        className="px-2 py-2 rounded bg-gray-200 text-gray-700 disabled:opacity-50 text-[13px]"
        onClick={() => onClick(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        type="button"
      >
        &lt;
      </button>
      <span className="text-[13px] font-medium mx-1">
        Page {currentPage} of {totalPages}
      </span>
      <button
        className="px-2 py-2 rounded bg-gray-200 text-gray-700 disabled:opacity-50 text-[13px]"
        onClick={() => onClick(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        type="button"
      >
        &gt;
      </button>
      <span className="ml-2 text-[13px] text-gray-500">{totalItems} records</span>
    </div>
  </div>
);

export default MobileListFooter;
